import { useState, type ReactNode } from 'react'
import { X, Lightbulb } from 'lucide-react'

const HINT_PREFIX = 'kobsign-hint-'

interface InlineHintProps {
  hintKey: string
  children: ReactNode
  /** Show pulsing icon to draw attention (default: false) */
  pulse?: boolean
}

/**
 * Dismissable contextual hint box.
 * Once dismissed via the X button, the hint stays hidden (localStorage).
 *
 * Visual upgrades:
 * - Gradient left border (primary → accent)
 * - Optional pulsing lightbulb icon
 * - Slide-down + fade-in entrance animation
 */
export function InlineHint({ hintKey, children, pulse = false }: InlineHintProps) {
  const storageKey = `${HINT_PREFIX}${hintKey}`
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(storageKey) === 'dismissed',
  )

  if (dismissed) return null

  const handleDismiss = () => {
    localStorage.setItem(storageKey, 'dismissed')
    setDismissed(true)
  }

  return (
    <div className="relative flex items-start gap-1.5 p-2 rounded-md bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/50 animate-slide-down overflow-hidden">
      {/* Gradient left border */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-primary-400 via-primary-500 to-accent-500 dark:from-primary-500 dark:via-primary-400 dark:to-accent-400" />

      <Lightbulb
        size={12}
        className={`shrink-0 mt-0.5 ml-1 text-primary-500 dark:text-primary-400 ${pulse ? 'animate-hint-pulse' : ''}`}
      />
      <p className="text-[11px] text-primary-700 dark:text-primary-300 flex-1 leading-relaxed">
        {children}
      </p>
      <button
        onClick={handleDismiss}
        className="shrink-0 p-0.5 rounded hover:bg-primary-100 dark:hover:bg-primary-800 text-primary-400 hover:text-primary-600 transition-colors"
        aria-label="Dismiss"
      >
        <X size={10} />
      </button>
    </div>
  )
}
